'use client';

import { createContext, useContext, useMemo, useState, ReactNode, useCallback } from 'react';
import { LoadingOverlay } from './LoadingOverlay';

export type LoadingOverlayContextValue = {
  /**
   * Show the loading overlay (full page by default)
   * @param fullPage - If true, overlay covers entire viewport. If false, covers parent container.
   */
  showLoading: (fullPage?: boolean) => void;
  /**
   * Hide the loading overlay
   */
  hideLoading: () => void;
  /**
   * Check if loading is currently active
   */
  isLoading: boolean;
  /**
   * Whether the overlay should be full page or component-level
   */
  isFullPage: boolean;
};

const LoadingOverlayContext = createContext<LoadingOverlayContextValue | null>(null);

export function LoadingOverlayProvider({ children }: Readonly<{ children: ReactNode }>) {
  const [isLoading, setIsLoading] = useState(false);
  const [isFullPage, setIsFullPage] = useState(true);

  const showLoading = useCallback((fullPage: boolean = true): void => {
    setIsFullPage(fullPage);
    setIsLoading(true);
  }, []);

  const hideLoading = useCallback((): void => {
    setIsLoading(false);
  }, []);

  const value: LoadingOverlayContextValue = useMemo(
    () => ({
      showLoading,
      hideLoading,
      isLoading,
      isFullPage,
    }),
    [showLoading, hideLoading, isLoading, isFullPage]
  );

  return (
    <LoadingOverlayContext.Provider value={value}>
      {children}
      {/* Only render full-page overlay in provider */}
      {isFullPage && <LoadingOverlay isLoading={isLoading} fullPage={true} />}
    </LoadingOverlayContext.Provider>
  );
}

export function useLoadingOverlay(): LoadingOverlayContextValue {
  const contextValue = useContext(LoadingOverlayContext);
  if (!contextValue) {
    throw new Error('useLoadingOverlay must be used within a LoadingOverlayProvider');
  }
  return contextValue;
}
